import React, { useState, useEffect } from "react";
import { Download, Plus, X, Eye } from "lucide-react";
import { getSanctionLetters, createSanctionLetter } from "../../services/AdminPortal/sanctionLetterAPI";
import AddSanctionLetterForm from "./AddSanctionLetterForm";
import SanctionLetterView from "./SanctionLetterView";

const SanctionLetterManagement = () => {
  const [showFormModal, setShowFormModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLetters();
  }, []);

  const fetchLetters = async () => {
    try {
      const data = await getSanctionLetters();
      setLetters(data || []);
    } catch (err) {
      console.error("Fetch sanction letters error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setShowFormModal(true);
  };

  const handleCreate = async () => {
    setShowFormModal(false);
    await fetchLetters();
  };

  const handleView = (letter) => {
    setSelectedLetter(letter);
    setShowViewModal(true);
  };

  const handleDownload = (letter) => {
    setSelectedLetter(letter);
    setShowViewModal(true);
  };

  return (
    <div className="p-6">
      <div className="bg-[#131313] border border-[#333] p-4 rounded-lg w-full">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white text-sm font-semibold">
            SANCTION LETTER MANAGEMENT
          </h2>
          <button
            onClick={handleAddClick}
            className="bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-semibold px-3 py-1 rounded flex items-center gap-1"
          >
            <Plus size={14} /> Add Sanction Letter
          </button>
        </div>

        {loading ? (
          <div className="text-center text-gray-400 py-8">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px] text-gray-200">
              <thead>
                <tr className="text-gray-400 text-[11px] border-b border-[#2a2a2a]">
                  <th className="text-left pb-2">LETTER NO</th>
                  <th className="text-left pb-2">APPLICANT</th>
                  <th className="text-center pb-2">DATE</th>
                  <th className="text-center pb-2">LOAN TYPE</th>
                  <th className="text-right pb-2">SANCTIONED AMOUNT</th>
                  <th className="text-center pb-2">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {letters.map((lt, idx) => (
                  <tr key={lt._id || idx} className="border-b border-[#2a2a2a]">
                    <td className="py-2">{lt.sanctionLetterNo}</td>
                    <td className="py-2">{lt.applicantName}</td>
                    <td className="text-center py-2">
                      {lt.sanctionDate
                        ? new Date(lt.sanctionDate).toLocaleDateString("en-GB")
                        : "-"}
                    </td>
                    <td className="text-center py-2">{lt.loanType}</td>
                    <td className="text-right py-2">
                      ₹{Number(lt.sanctionedAmount || 0).toLocaleString("en-IN")}
                    </td>
                    <td className="text-center py-2">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleView(lt)}
                          className="text-[#FFCA20] text-xs hover:underline flex items-center gap-1"
                        >
                          <Eye size={12} /> View
                        </button>
                        <button
                          onClick={() => handleDownload(lt)}
                          className="text-gray-400 text-xs hover:underline flex items-center gap-1"
                        >
                          <Download size={12} /> Download
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {letters.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center text-gray-400 py-8">
                      No sanction letters found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Letter Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#131313] p-6 rounded-lg w-full max-w-4xl shadow-lg border border-[#333]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-base font-semibold">
                Add Sanction Letter
              </h3>
              <button
                onClick={() => setShowFormModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <AddSanctionLetterForm
              onCancel={() => setShowFormModal(false)}
              onSave={handleCreate}
            />
          </div>
        </div>
      )}

      {/* View Letter Modal */}
      {showViewModal && selectedLetter && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#131313] p-6 rounded-lg w-full max-w-3xl shadow-lg border border-[#333] max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white ml-4 text-lg font-semibold">
                Sanction Letter Preview
              </h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-600 hover:text-black"
              >
                <X size={20} />
              </button>
            </div>

            <SanctionLetterView letter={selectedLetter} allowDownload={true} />
          </div>
        </div>
      )}
    </div>
  );
};

export default SanctionLetterManagement;